import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { FamilyService } from '../family.service';
import { SaveFamilyPayload } from '../../../models/family.model';

@Component({
  selector: 'app-create-family',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule, CheckboxModule, InputNumberModule],
  templateUrl: './create-family.component.html'
})
export class CreateFamilyComponent implements OnInit, OnDestroy {
  @Input() id: number | null = null;
  form!: FormGroup;
  hasLinkedProducts = false;
  originalPrices: { priceSelling: number; priceTerm: number | null; priceWholeSale: number | null } | null = null;
  @Output() formSubmitted = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private familyService: FamilyService
  ) {}

  ngOnDestroy(): void {
    this.modalClosed.emit();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      description: ['', Validators.required],
      priceSelling: [0, [Validators.required, Validators.min(0)]],
      priceTerm: [0, [Validators.min(0)]],
      priceWholeSale: [0, [Validators.min(0)]],
      active: [true]
    });

    if (this.id) {
      this.loadData();
    }
  }

  private loadData(): void {
    this.familyService.getById(this.id as number).subscribe({
      next: (res) => {
        const data = res.data;
        if (!data) {
          return;
        }

        this.originalPrices = {
          priceSelling: Number(data.priceSelling ?? 0),
          priceTerm: data.priceTerm != null ? Number(data.priceTerm) : null,
          priceWholeSale: data.priceWholeSale != null ? Number(data.priceWholeSale) : null
        };
        this.hasLinkedProducts = (data.products?.length ?? 0) > 0;
        this.form.patchValue({
          description: data.description,
          priceSelling: data.priceSelling,
          priceTerm: data.priceTerm ?? 0,
          priceWholeSale: data.priceWholeSale ?? 0,
          active: data.active
        });
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  private static normOptionalPrice(v: number | null | undefined): number | null {
    if (v == null || v === 0) {
      return null;
    }
    return Number(v);
  }

  private pricesChanged(payload: SaveFamilyPayload): boolean {
    if (!this.originalPrices) {
      return false;
    }
    return (
      payload.priceSelling !== this.originalPrices.priceSelling ||
      CreateFamilyComponent.normOptionalPrice(payload.priceTerm) !==
        CreateFamilyComponent.normOptionalPrice(this.originalPrices.priceTerm) ||
      CreateFamilyComponent.normOptionalPrice(payload.priceWholeSale) !==
        CreateFamilyComponent.normOptionalPrice(this.originalPrices.priceWholeSale)
    );
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => this.form.get(key)?.markAsTouched());
      return;
    }

    const termRaw = Number(this.form.get('priceTerm')?.value ?? 0);
    const wholeRaw = Number(this.form.get('priceWholeSale')?.value ?? 0);

    const payload: SaveFamilyPayload = {
      description: this.form.get('description')?.value?.trim(),
      priceSelling: Number(this.form.get('priceSelling')?.value ?? 0),
      priceTerm: termRaw === 0 ? null : termRaw,
      priceWholeSale: wholeRaw === 0 ? null : wholeRaw,
      active: !!this.form.get('active')?.value
    };

    if (
      this.id &&
      this.hasLinkedProducts &&
      this.originalPrices &&
      this.pricesChanged(payload) &&
      !confirm(
        'Esta alteração atualizará os preços de venda, prazo e atacado dos produtos vinculados a esta família. Deseja continuar?'
      )
    ) {
      return;
    }

    if (this.id) {
      this.familyService.update(this.id, payload).subscribe({
        next: () => this.formSubmitted.emit()
      });
      return;
    }

    this.familyService.create(payload).subscribe({
      next: () => this.formSubmitted.emit()
    });
  }
}
